import {
  DEFAULT_JEV_MODEL,
  MODELS_BY_PROVIDER,
  defaultReasoningFor,
} from "@/lib/mapper/ai-models";
import type { AIProvider, Rule, Templatable } from "@/lib/mapper/types";
import type { CellModule } from "@/modules/_shared/contract";
import { AI_RULE_PLACEHOLDER } from "@/modules/_shared/engine";
import { toTemplateValue } from "@/modules/_shared/templateValue";

// AI default — reproduces MapperInspector.emptyRule("ai") exactly.
function defaultAIRuleFor(provider: AIProvider): Rule {
  const preferred =
    provider === "openai"
      ? "gpt-5.5"
      : provider === "anthropic"
        ? "claude-sonnet-4-6"
        : undefined;
  const model =
    (preferred && MODELS_BY_PROVIDER[provider].find((m) => m.id === preferred)?.id) ??
    MODELS_BY_PROVIDER[provider][0]!.id;
  return {
    kind: "ai",
    provider,
    model,
    prompt: "",
    reasoning: defaultReasoningFor(provider, model),
  };
}

export const aiModule: CellModule = {
  kind: "ai",
  id: "ai",
  capability: "cell",
  hidden: true,
  name: "Write with AI",
  tooltip: "Let AI write this for you",
  description: "Lets AI write this column for you using the other columns, like a product description.",
  category: "AI",
  tags: ["llm", "generate", "ai", "anthropic", "openai"],
  icon: { kind: "lucide", name: "Sparkles" },
  tone: "teal",
  defaultConfig: () => defaultAIRuleFor("anthropic"),
  resolve: () => AI_RULE_PLACEHOLDER,
  templateStrings: (rule) => (rule.kind === "ai" ? [rule.prompt, rule.system ?? ""] : []),
};

export const anthropicPicker: CellModule = {
  ...aiModule,
  id: "ai:anthropic",
  hidden: false,
  pickerOnly: true,
  name: "Write with Claude",
  tooltip: "Use Claude AI to write this",
  description:
    "Uses Claude AI to write this column. You can edit the wording after.",
  tags: ["llm", "claude", "anthropic", "ai", "generate"],
  icon: { kind: "brand", name: "anthropic" },
  defaultConfig: () => defaultAIRuleFor("anthropic"),
};

export const openaiPicker: CellModule = {
  ...aiModule,
  id: "ai:openai",
  hidden: false,
  pickerOnly: true,
  name: "Write with ChatGPT",
  tooltip: "Use ChatGPT to write this",
  description:
    "Uses ChatGPT to write this column. You can edit the wording after.",
  tags: ["llm", "gpt", "openai", "ai", "generate"],
  icon: { kind: "brand", name: "openai" },
  defaultConfig: () => defaultAIRuleFor("openai"),
};

export const openrouterPicker: CellModule = {
  ...aiModule,
  id: "ai:openrouter",
  hidden: false,
  pickerOnly: true,
  name: "Write with OpenRouter",
  tooltip: "Cheaper AI like DeepSeek, Qwen or Gemini",
  description:
    "Uses a model on OpenRouter (DeepSeek, Qwen, Gemini and more) to write this column, usually for a fraction of the price.",
  tags: ["llm", "openrouter", "deepseek", "qwen", "gemini", "cheap", "ai", "generate"],
  icon: { kind: "lucide", name: "Route" },
  defaultConfig: () => defaultAIRuleFor("openrouter"),
};

/** A fresh Jev decision rule: tag picking by default. */
export function defaultJevRule(): Rule {
  return {
    kind: "ai",
    provider: "openrouter",
    model: DEFAULT_JEV_MODEL,
    prompt: "",
    decision: { kind: "tags", tags: [], threshold: 0.5, separator: ", " },
  };
}

export const jevPicker: CellModule = {
  ...aiModule,
  id: "ai:jev",
  hidden: false,
  pickerOnly: true,
  name: "Tag & classify with Jev",
  tooltip: "Pick tags, one option, or yes/no with Jev",
  description:
    "Jev reads the product details and answers typed questions: which tags apply, which option fits best, or yes/no. It never writes prose, so answers are predictable and cost a fraction of a cent.",
  tags: ["jev", "typesafe", "tags", "classify", "category", "decision", "openrouter", "ai"],
  icon: { kind: "lucide", name: "Tags" },
  defaultConfig: defaultJevRule,
};

const HANDLE_SYSTEM =
  "You write Shopify product URL handles. Reply with the handle only: 3 to 6 " +
  "lowercase words joined by hyphens, most important keywords first (product " +
  "type, material, colour, style). No filler words, no quotes, no explanation.";

/**
 * An AI rule that writes an SEO handle. `format: "slug"` runs the answer
 * through the smart slugify, so the cell is always a valid Handle.
 */
export function aiHandleRule(input: Templatable = ""): Rule {
  const provider: AIProvider = "openrouter";
  const model = MODELS_BY_PROVIDER[provider][0]!.id;
  return {
    kind: "ai",
    provider,
    model,
    system: HANDLE_SYSTEM,
    prompt: [{ t: "text", v: "Product: " }, ...toTemplateValue(input)],
    reasoning: defaultReasoningFor(provider, model),
    maxTokens: 64,
    format: "slug",
  };
}

export const aiHandlePicker: CellModule = {
  ...aiModule,
  id: "ai:handle",
  hidden: false,
  pickerOnly: true,
  name: "SEO handle with AI",
  tooltip: "AI writes a short, keyword-first URL handle",
  description:
    "AI turns the product name into a short, search-friendly URL handle (like cotton-kurta-blue). The answer is always cleaned into a valid Handle.",
  tags: ["handle", "slug", "seo", "url", "ai", "openrouter"],
  icon: { kind: "lucide", name: "Link2" },
  defaultConfig: () => aiHandleRule(""),
};
